import { useState, useEffect, useCallback } from 'react';
import { request } from '../../../shared/services/api';
import type { PaginatedResponse, SimpleResponse } from '../types/requests.types';

interface UseRequestsConfigOptions {
  paginated?: boolean;
  autoFetch?: boolean;
  queryParams?: Record<string, string>;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface UseRequestsConfigReturn<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  meta: PaginationMeta | null;
  page: number;
  setPage: (p: number) => void;
  crear: (data: Partial<T>) => Promise<T>;
  actualizar: (id: string, data: Partial<T>) => Promise<T>;
  eliminar: (id: string) => Promise<void>;
  refetch: () => void;
}

export function useRequestsConfig<T extends { id: string }>(
  endpoint: string,
  options?: UseRequestsConfigOptions,
): UseRequestsConfigReturn<T> {
  const { paginated = false, autoFetch = true, queryParams } = options ?? {};
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (paginated) {
      params.set('page', String(page));
      params.set('porPagina', '100');
    }
    if (queryParams) {
      for (const [k, v] of Object.entries(queryParams)) {
        if (v) params.set(k, v);
      }
    }
    const qs = params.toString();
    return qs ? `${endpoint}?${qs}` : endpoint;
  }, [endpoint, paginated, page, queryParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = buildQuery();
      if (paginated) {
        const res = await request<PaginatedResponse<T>>(url);
        setItems(res.data);
        setMeta(res.meta);
      } else {
        const res = await request<SimpleResponse<T>>(url);
        setItems(res.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, paginated]);

  useEffect(() => {
    if (autoFetch) fetchData();
  }, [autoFetch, fetchData]);

  const crear = useCallback(
    async (data: Partial<T>) => {
      const res = await request<{ data: T }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchData();
      return res.data;
    },
    [endpoint, fetchData],
  );

  const actualizar = useCallback(
    async (id: string, data: Partial<T>) => {
      const res = await request<{ data: T }>(`${endpoint}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchData();
      return res.data;
    },
    [endpoint, fetchData],
  );

  const eliminar = useCallback(
    async (id: string) => {
      await request<void>(`${endpoint}/${id}`, { method: 'DELETE' });
      await fetchData();
    },
    [endpoint, fetchData],
  );

  return {
    items,
    loading,
    error,
    meta,
    page,
    setPage,
    crear,
    actualizar,
    eliminar,
    refetch: fetchData,
  };
}
